import { useState, useCallback } from "react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadImage = useCallback(async (file) => {
    if (!file) return null;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, GIF, or WebP.");
      return null;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return null;
    }

    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/upload/image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Upload failed");
      }

      const data = await response.json();
      setProgress(100);
      toast.success("Image uploaded successfully!");
      
      // Return the full URL to the uploaded file
      return `${API}/files/${data.file_id}`;
    } catch (error) {
      toast.error(error.message || "Failed to upload image");
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  const uploadMultipleImages = useCallback(async (files) => {
    const urls = [];
    for (const file of files) {
      const url = await uploadImage(file);
      if (url) urls.push(url);
    }
    return urls;
  }, [uploadImage]);

  return {
    uploadImage,
    uploadMultipleImages,
    uploading,
    progress,
  };
};

export default useImageUpload;
